import React, { useEffect, useState } from "react";

// Orderart's public Zoho Desk knowledge base (https://orderart.zohodesk.com/portal/en/kb/orderart-knowledge-base/general).
// This is Zoho's public Help Center API - unauthenticated by design (it's what powers that public
// page itself) and CORS-open to any origin, so it's called directly from here rather than proxied
// through our own backend. portalId/categoryId are stable public identifiers for that portal/
// section, not secrets - the same values are visible in that page's own network requests.
const ZOHO_PORTAL_ID = "edbsn5abcb0a495a0bda4d81e3d0ecb645ca61ca85cba578a5a8c3862d945bc48ecd7";
const ZOHO_CATEGORY_ID = "843704000000224215"; // "General" section
const ZOHO_API_ROOT = "https://orderart.zohodesk.com/portal/api";

const ArticleDetail = ({ article, onBack }) => {
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(
      `${ZOHO_API_ROOT}/kbArticles/articleByPermalink?portalId=${ZOHO_PORTAL_ID}&permalink=${article.permalink}&locale=en`
    )
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setFull(data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [article.permalink]);

  // allow-same-origin without allow-scripts: JS still can never execute (that needs
  // allow-scripts, which this deliberately omits), but a fully opaque-origin sandbox (neither
  // flag) blocks the article's own images from loading too - this keeps the actual injection
  // risk (script execution) fully neutralized while still rendering images/inline styles,
  // without needing an HTML-sanitizing library.
  //
  // White background rather than the app's own dark theme: the article HTML comes from Zoho
  // with its own inline text-color styles (mostly near-black), authored against a white page on
  // their own site - a dark body here would make that text unreadable rather than restyle it.
  const srcDoc = full
    ? `<!doctype html><html><head><meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          body { margin:0; padding:16px; background:#fff; color:#1a1a1a; font-family:"Open Sans",sans-serif; font-size:16px; line-height:1.5; }
          img { max-width:100%; height:auto; }
          a { color:#c52432; }
        </style>
      </head><body>${full.answer || ""}</body></html>`
    : "";

  return (
    <div className="cardStyle p-3">
      <button type="button" className="btn iconBtn smBtn btnLight mb-3" onClick={onBack}>
        ← Back to Articles
      </button>
      <h4 className="mb-1">{article.title}</h4>
      {full && (
        <div style={{ fontSize: "12px", opacity: 0.6, marginBottom: "12px" }}>
          Last updated {new Date(full.modifiedTime).toLocaleDateString()}
        </div>
      )}
      {loading ? (
        <p>Loading…</p>
      ) : (
        <iframe
          title={article.title}
          srcDoc={srcDoc}
          sandbox="allow-same-origin"
          style={{ width: "100%", minHeight: "70vh", border: "none", borderRadius: "8px" }}
        />
      )}
    </div>
  );
};

const ArticlesPanel = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetch(
      `${ZOHO_API_ROOT}/kbArticles?portalId=${ZOHO_PORTAL_ID}&categoryId=${ZOHO_CATEGORY_ID}&from=1&limit=100&includeChildCategoryArticles=false&locale=en`
    )
      .then((res) => res.json())
      .then((data) => setArticles(data?.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (selected) {
    return <ArticleDetail article={selected} onBack={() => setSelected(null)} />;
  }

  if (loading) {
    return <p>Loading…</p>;
  }

  if (error) {
    return (
      <div className="cardStyle p-4" style={{ opacity: 0.85 }}>
        Couldn't load articles right now. Check the connection and try again.
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-2">
      {articles.map((article) => (
        <button
          type="button"
          key={article.id}
          className="cardStyle p-3 text-start"
          style={{ border: "none", width: "100%" }}
          onClick={() => setSelected(article)}
        >
          <div className="d-flex justify-content-between align-items-center gap-3">
            <div>
              <div style={{ fontWeight: 600 }}>{article.title}</div>
              <div style={{ fontSize: "13px", opacity: 0.7, marginTop: "4px" }}>{article.summary}</div>
            </div>
            <span style={{ opacity: 0.5, flexShrink: 0 }}>›</span>
          </div>
        </button>
      ))}
    </div>
  );
};

export default ArticlesPanel;
